import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    History, Loader2, AlertTriangle, MapPin,
    ArrowUpRight, ArrowDownRight, Minus, BarChart3
} from 'lucide-react';
import ComparisonChart from './components/ComparisonChart';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5022';

const YearComparisonTab = ({ selectedDevice }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [comparisonData, setComparisonData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (selectedDevice) {
            loadComparison();
        } else {
            setComparisonData(null);
        }
    }, [selectedDevice]);

    const loadComparison = async () => {
        if (!selectedDevice) return;
        setIsLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');

            const response = await axios.get(`${API_URL}/api/analysis/comparison`, {
                params: {
                    deviceName: selectedDevice.name,
                    deviceType: selectedDevice.deviceType,
                    period: 'year',
                },
                headers: { Authorization: `Bearer ${token}` },
            });

            setComparisonData(response.data);
        } catch (err) {
            console.error('Ошибка сравнения год к году:', err);
            setError(err.response?.data?.message || 'Не удалось выполнить сравнение');
        } finally {
            setIsLoading(false);
        }
    };

    const renderChangeIndicator = (changePercent) => {
        if (changePercent === null || changePercent === undefined) {
            return <Minus className="w-4 h-4 text-slate-500" />;
        }
        if (changePercent > 1) {
            return (
                <span className="flex items-center gap-1 text-red-400 font-semibold">
                    <ArrowUpRight className="w-4 h-4" />
                    +{changePercent.toFixed(1)}%
                </span>
            );
        }
        if (changePercent < -1) {
            return (
                <span className="flex items-center gap-1 text-blue-400 font-semibold">
                    <ArrowDownRight className="w-4 h-4" />
                    {changePercent.toFixed(1)}%
                </span>
            );
        }
        return (
            <span className="flex items-center gap-1 text-slate-400">
                <Minus className="w-4 h-4" />
                {changePercent.toFixed(1)}%
            </span>
        );
    };

    // Заглушка: устройство не выбрано
    if (!selectedDevice) {
        return (
            <div className="bg-slate-800/50 border border-slate-700 border-dashed rounded-xl p-12 text-center">
                <MapPin className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">
                    Выберите устройство из списка выше для начала сравнения
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Информационная панель */}
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h3 className="text-white font-semibold flex items-center gap-2">
                            <History className="w-5 h-5 text-purple-400" />
                            Сравнение: текущий год vs прошлый год
                        </h3>
                        <p className="text-slate-400 text-xs mt-1">
                            Показания за последние 30 дней текущего года сравниваются с аналогичным периодом прошлого года.
                        </p>
                    </div>
                    <button
                        onClick={loadComparison}
                        disabled={isLoading}
                        className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium transition disabled:opacity-50 flex items-center gap-2 self-start"
                    >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <History className="w-4 h-4" />}
                        Обновить
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    {error}
                </div>
            )}

            {isLoading && (
                <div className="flex items-center justify-center py-12 text-slate-400">
                    <Loader2 className="w-6 h-6 animate-spin mr-2" />
                    Выполняется сравнение...
                </div>
            )}

            {comparisonData && !isLoading && (
                <div className="space-y-4">
                    {/* Периоды */}
                    <div className="flex flex-wrap gap-3 px-1">
                        <span className="px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 text-purple-300 rounded-lg text-xs font-medium">
                            Текущий: {comparisonData.currentPeriodLabel}
                        </span>
                        <span className="px-3 py-1.5 bg-slate-500/10 border border-slate-500/30 text-slate-300 rounded-lg text-xs font-medium">
                            Прошлый год: {comparisonData.previousPeriodLabel}
                        </span>
                    </div>

                    {/* Таблица изменений */}
                    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                        <div className="px-4 py-3 bg-slate-700/50 border-b border-slate-700">
                            <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                                <BarChart3 className="w-4 h-4 text-purple-400" />
                                Сводка изменений (год к году)
                            </h4>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-slate-700/50">
                                        <th className="px-4 py-3 text-left text-slate-300 font-medium">Параметр</th>
                                        <th className="px-4 py-3 text-right text-slate-300 font-medium">
                                            {comparisonData.currentPeriodLabel || 'Текущий'}
                                        </th>
                                        <th className="px-4 py-3 text-right text-slate-300 font-medium">
                                            {comparisonData.previousPeriodLabel || 'Прошлый год'}
                                        </th>
                                        <th className="px-4 py-3 text-right text-slate-300 font-medium">Изменение</th>
                                        <th className="px-4 py-3 text-right text-slate-300 font-medium">Абс. Δ</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(comparisonData.parameters || []).map((param, idx) => (
                                        <tr key={idx} className="border-t border-slate-700 hover:bg-slate-700/30">
                                            <td className="px-4 py-3 text-white font-medium">{param.name}</td>
                                            <td className="px-4 py-3 text-right text-white font-mono">
                                                {param.currentAvg?.toFixed(3) ?? '—'}
                                            </td>
                                            <td className="px-4 py-3 text-right text-slate-300 font-mono">
                                                {param.previousAvg?.toFixed(3) ?? '—'}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                {renderChangeIndicator(param.changePercent)}
                                            </td>
                                            <td className="px-4 py-3 text-right text-slate-300 font-mono">
                                                {param.absoluteChange?.toFixed(3) ?? '—'} {param.unit}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Графики */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {(comparisonData.charts || []).map((chart, idx) => (
                            <ComparisonChart
                                key={idx}
                                title={chart.title}
                                data={chart.data}
                                unit={chart.unit}
                                chartType="line"
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default YearComparisonTab;